import React from "react";
import { IoClose } from "react-icons/io5";
import { useAuth } from "../../../../context/AuthContext";
import approvedStamp from "../../../../assets/images/Approvals/Approved.png";
import { useTranslation } from "react-i18next";

const ViewApproveAgreementDetailsModal = ({ isOpen, onClose, data, onApprove }) => {
    const {t}=useTranslation();
    const { user } = useAuth();

    if (!isOpen || !data) return null;

    // Logic: Only show Approve button for Officers who didn't create the record
    const isOfficer = user?.designation?.toLowerCase().includes("officer");
    const canApprove = isOfficer && !data.isApproved && data.createdBy !== user?.userId;

    return (
        <div className="staff-modal-overlay">
            <div className="staff-modal" style={{ width: '1000px', maxWidth: '95%' }}>

                {/* Header - Centered with blue background per system standard */}
                <div className="staff-modal-header" style={{ background: '#a0adf1', padding: '10px 15px' }}>
                    <div className="staff-modal-title" style={{ color: 'white', width: '100%', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>
                        {t('approvals.approveAgreementProcess')} {t('common.details')}
                    </div>
                    <button className="staff-modal-close" onClick={onClose} style={{ color: 'white' }}>
                        <IoClose size={22} />
                    </button>
                </div>

                <div className="staff-form-body p-8 bg-white relative">

                    {/* Section 1: Search Tender (image_aa157a.png) */}
                    <div className="border rounded-md p-6 mb-6 relative bg-white">
                        <h6 className="absolute -top-2 left-4 bg-white px-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                            {t('approvals.searchTender')}
                        </h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
                            <ModalDetailRow label={t('tables.tenderProcess.tenderId')} value={data.tenderId} isRequired />
                            <ModalDetailRow label={t('tables.tenderProcess.tenderDate')} value={data.tenderDate} />
                            <div className="col-span-2">
                                <ModalDetailRow label={t('tables.workName')} value={data.workTitle} fullWidth />
                            </div>
                            <ModalDetailRow label={t('tables.tenderProcess.tenderValue')} value={data.tenderValue ? Number(data.tenderValue).toFixed(2) : "0.00"} />
                        </div>
                    </div>

                    {/* Section 2: Contractor (image_aa157a.png) */}
                    <div className="border rounded-md p-6 mb-6 relative bg-white">
                        <h6 className="absolute -top-2 left-4 bg-white px-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                            {t('approvals.contractor')}
                        </h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
                            <div className="col-span-2">
                                <ModalDetailRow label={t('tables.tenderProcess.bidderName')} value={data.bidderName} isRequired fullWidth />
                            </div>
                            <ModalDetailRow label="Above / Below %" value={data.aboveBelow} isRequired />
                            <ModalDetailRow label={t('tables.tenderProcess.bidAmount')} value={data.bidAmount ? Number(data.bidAmount).toFixed(2) : "0.00"} isRequired />
                        </div>
                    </div>

                    {/* Section 3: Agreement Details (image_aa157a.png) */}
                    <div className="border rounded-md p-6 mb-4 relative bg-white">
                        <h6 className="absolute -top-2 left-4 bg-white px-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                            {t('approvals.agreement')}
                        </h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
                            <ModalDetailRow label={t('approvals.agreementNo')} value={data.agreementNo} isRequired />
                            <ModalDetailRow label={t('approvals.agreementDate')} value={data.agreementDate} isRequired />
                            <ModalDetailRow label="BPG" value={data.bpg} isRequired />
                            <ModalDetailRow label={t('common.date')} value={data.bpgDate} isRequired />
                        </div>
                    </div>

                    {/* Approval Section: Stamp vs Action Button */}
                    <div className="flex justify-center mt-6 min-h-[100px] items-center">
                        {data.isApproved ? (
                            <div className="relative">
                                <img 
                                    src={approvedStamp} 
                                    alt="Approved" 
                                    className="w-48 object-contain opacity-90" 
                                    style={{ transform: 'rotate(-5deg)' }} 
                                />
                            </div>
                        ) : (
                            canApprove && (
                                <button
                                    className="staff-btn-save"
                                    onClick={() => onApprove(data)}
                                >
                                    Approve
                                </button>
                            )
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="staff-form-footer border-t p-4 flex justify-end bg-gray-50">
                    <button onClick={onClose} className="staff-btn-save">
                        {t('common.close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Helper component for consistent Modal Detail Rows
 */
const ModalDetailRow = ({ label, value, isRequired, fullWidth }) => (
    <div className={`flex items-center text-[13px] ${fullWidth ? 'w-full' : ''}`}>
        <span className="w-40 text-right font-semibold text-gray-700">
            {label} {isRequired && <span className="text-red-500">(*)</span>}
        </span>
        <span className="px-3 text-gray-800 font-bold">:</span>
        <div className="flex-1 bg-gray-50 border border-gray-200 p-1.5 rounded font-bold text-gray-900 min-h-[32px]">
            {value || ""}
        </div>
    </div>
);

export default ViewApproveAgreementDetailsModal;